import React from 'react'
import {
    Theme,
    Modal,
    Paper,
    Slide,
    Button,
    makeStyles,
    Popover,
    Typography,
} from '@material-ui/core'
import {
    DeliverableValue,
    ResidentTermDirectoryRow,
    CriteriaGroup,
} from '../../../../models/DocumentAudit/types'
import { Container, MessageForm } from '../../../../components'

import {
    useResidentTermDirectoryDetail,
    useDeliverable,
} from '../../../../hooks/documentAudit'
import { ModalHeader } from './ModalHeader'
import { EntityList } from './EntityList'
import { DocumentViewer } from './DocumentViewer'
import { MessageDetail } from '../../../../models/Messaging/types'
import { getEntityClassDisplayName } from '../../../../models/DocumentAudit/services'

interface Props {
    theme: Theme
    open: boolean
    onClose: () => void
    entityValue: DeliverableValue | null
    row: ResidentTermDirectoryRow | null
    criteriaGroup: CriteriaGroup | undefined
    onEntitySelect: (entityValue: DeliverableValue) => void
    deliverableId: number
    updateDeliverableValueMessageCount: (
        rowId: number,
        valueId: number,
        newCount: number,
    ) => void
}

export const RowDetailModal = (props: Props) => {
    const {
        theme,
        open,
        onClose,
        entityValue,
        row,
        criteriaGroup,
        onEntitySelect,
        deliverableId,
        updateDeliverableValueMessageCount,
    } = props

    const getDimensions = () => {
        return {
            modalHeight: window.innerHeight * 0.9,
            modalWidth: window.innerWidth * 0.75,
        }
    }

    const [dimensions, setDimensions] = React.useState(getDimensions())

    React.useEffect(() => {
        const handleResize = () => {
            setDimensions(getDimensions())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const {
        directory,
        isLoadingDirectory,
        errorDirectory,
    } = useResidentTermDirectoryDetail(deliverableId, row?.id ?? -1)

    // Reset all state when row changes
    React.useEffect(() => {
        if (row) {
            setSelectedDocumentId(null)
            setSelectedPage(null)
            setSelectedValueId(undefined)
            setSelectedCoordinates(null)
        }
    }, [row?.id])

    const [selectedDocumentId, setSelectedDocumentId] = React.useState<
        number | null
    >(null)
    const [selectedPage, setSelectedPage] = React.useState<number | null>(null)
    const [selectedValueId, setSelectedValueId] = React.useState<number>()
    const [selectedCoordinates, setSelectedCoordinates] = React.useState<
        string | null
    >(null)
    const [chatDrawerOpen, setChatDrawerOpen] = React.useState(false)
    const [activeChannelId, setActiveChannelId] = React.useState<number | null>(
        null,
    )
    const [
        selectedEntityTitle,
        setSelectedEntityTitle,
    ] = React.useState<string>('')

    const handleDocumentSelect = (
        documentId: number,
        pageNumber: number | null,
        valueId: number,
        coordinates: string | null,
    ) => {
        setSelectedDocumentId(documentId)
        setSelectedPage(pageNumber)
        setSelectedValueId(valueId)
        setSelectedCoordinates(coordinates)
    }

    const handleOpenChat = (
        channelId: number,
        entityTitle: string,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        setActiveChannelId(channelId)
        setSelectedEntityTitle(entityTitle)
        setChatDrawerOpen(true)
    }

    const handleCloseChat = () => {
        setChatDrawerOpen(false)
    }

    const handleMessageCreated = (message: MessageDetail) => {
        if (entityValue && row) {
            const newCount = (entityValue.message_count || 0) + 1

            // Update the React Query cache
            updateDeliverableValueMessageCount(row.id, entityValue.id, newCount)

            // Update the local modal state
            const updatedEntityValue = {
                ...entityValue,
                message_count: newCount,
            }
            onEntitySelect(updatedEntityValue)
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                setSelectedDocumentId(null)
                setSelectedPage(null)
                setSelectedValueId(undefined)
                setSelectedCoordinates(null)
                onClose()
            }}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper>
                    <Container
                        style={{
                            height: dimensions.modalHeight,
                            width: dimensions.modalWidth,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            padding: theme.spacing(2),
                        }}
                    >
                        <ModalHeader row={row} theme={theme} />

                        <Container
                            style={{
                                flex: 1,
                                display: 'flex',
                                gap: theme.spacing(2),
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            <EntityList
                                criteriaGroup={criteriaGroup}
                                row={directory ?? null}
                                entityValue={entityValue}
                                onEntitySelect={onEntitySelect}
                                onDocumentSelect={handleDocumentSelect}
                                theme={theme}
                                selectedDocumentId={selectedDocumentId}
                                selectedPage={selectedPage}
                                selectedValueId={selectedValueId}
                                selectedCoordinates={selectedCoordinates}
                                onOpenChat={handleOpenChat}
                            />

                            <DocumentViewer
                                isLoadingDirectory={isLoadingDirectory}
                                directory={directory ?? null}
                                selectedDocumentId={selectedDocumentId}
                                selectedPage={selectedPage}
                                selectedCoordinates={selectedCoordinates}
                            />
                        </Container>

                        <Container
                            style={{
                                marginTop: theme.spacing(2),
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button onClick={onClose} variant="contained">
                                Close
                            </Button>
                        </Container>

                        {/* message popover */}
                        <Popover
                            open={chatDrawerOpen}
                            onClose={handleCloseChat}
                            anchorReference="none"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                marginRight: theme.spacing(4),
                            }}
                            PaperProps={{
                                style: {
                                    width: 400,
                                    maxHeight: 800,
                                    marginBottom: theme.spacing(2),
                                    overflow: 'auto',
                                    boxShadow: theme.shadows[8],
                                    borderRadius: theme.shape.borderRadius,
                                    border: `12px solid ${theme.palette.divider}`,
                                    flexDirection: 'column',
                                    flex: 1,
                                },
                            }}
                        >
                            <Container
                                style={{
                                    padding: theme.spacing(2),
                                    display: 'flex',
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="h6" noWrap>
                                    {getEntityClassDisplayName(
                                        selectedEntityTitle,
                                    ) || 'Messages'}
                                </Typography>
                                <Button
                                    onClick={handleCloseChat}
                                    variant="text"
                                >
                                    Close
                                </Button>
                            </Container>

                            <MessageForm
                                theme={theme}
                                channelId={activeChannelId ?? undefined}
                                secondaryChannelId={null}
                                afterCreateMessage={handleMessageCreated}
                            />
                        </Popover>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
